/**
 * Typed bindings for /api/inviter — campaign CRUD + run.
 *
 * Strategy: manual add to channel via Telethon InviteToChannelRequest.
 * NOT DM-invite. Long-dormant users are preferred targets.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type CampaignStatus =
  | "queued"
  | "running"
  | "paused"
  | "done"
  | "cancelled"
  | "failed";

export type CampaignStrategy =
  | "manual_add"
  | "dm_message"
  | "channel_forward"
  | "story_mention";

export type AttemptStatus =
  | "pending"
  | "added"
  | "privacy_restricted"
  | "peer_flood"
  | "admin_required"
  | "flood_wait"
  | "channel_private"
  | "already_member"
  | "deleted_user"
  | "too_many_users"
  | "other_failed"
  | "skipped";

export interface CampaignSummary {
  id: string;
  name: string;
  target_channel_input: string;
  target_channel_tg_id: number | null;
  target_channel_title: string | null;
  strategy: CampaignStrategy;
  status: CampaignStatus;
  error: string | null;
  total_targets: number;
  attempted: number;
  succeeded: number;
  failed_privacy: number;
  failed_peer_flood: number;
  failed_admin: number;
  failed_other: number;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  /** Why a queued campaign is not sending right now; null = nothing holds it. */
  waiting_reason?: "quiet_hours" | "accounts_resting" | "daily_limit" | "pausing" | null;
}

export interface CampaignDetail extends CampaignSummary {
  filter_json: Record<string, unknown>;
  max_per_account_per_day: number;
  min_delay_seconds: number;
  max_delay_seconds: number;
  dm_template: string | null;
  forward_source_channel: string | null;
  forward_message_id: number | null;
  forward_followup_text: string | null;
  forward_quote_text: string | null;
  forward_quote_offset: number | null;
  story_media_asset_id: string | null;
  story_caption: string | null;
  story_cta_channel_post_url: string | null;
  story_batch_size: number | null;
}

export interface CreateCampaignPayload {
  name: string;
  target_channel: string;
  strategy?: CampaignStrategy;
  dm_template?: string;
  forward_post_url?: string;
  forward_followup_text?: string;
  forward_quote_text?: string;
  story_media_asset_id?: string;
  story_caption?: string;
  story_cta_channel_post_url?: string;
  story_batch_size?: number;
  channel_id?: string;
  /** Source channels, unioned: a person in any of them is a target. */
  channel_ids?: string[];
  /** Imported lists as sources — the label given at upload (or batch id). */
  import_sources?: string[];
  min_activity?: number;
  has_username?: boolean;
  no_bots?: boolean;
  is_premium?: boolean;
  online_recently?: boolean;
  language?: string;
  exclude_contacted_days?: number;
  exclude_toxic_sources?: boolean;
  max_per_account_per_day?: number;
  min_delay_seconds?: number;
  max_delay_seconds?: number;
  max_targets?: number;
}

export interface RunResult {
  campaign_id: string;
  attempted: number;
  succeeded: number;
  failed: number;
  paused_reason: string | null;
}

export interface AttemptOut {
  id: string;
  scraped_user_id: string;
  tg_user_id: number;
  tg_username: string | null;
  target_first_name: string | null;
  account_id: string | null;
  account_label: string | null;
  status: AttemptStatus;
  error: string | null;
  attempted_at: string | null;
  created_at: string;
}

export interface AttemptsPage {
  total: number;
  items: AttemptOut[];
}

export interface PostPreview {
  channel_username: string;
  message_id: number;
  channel_title: string | null;
  text: string;
  has_media: boolean;
  resolved_via_account_id: string | null;
}

export function usePreviewPost() {
  return useMutation({
    mutationFn: (post_url: string) =>
      api.post<PostPreview>("/api/inviter/preview-post", { post_url }),
  });
}

export const INVITE_CAMPAIGNS_KEY = ["invite_campaigns"] as const;

export function useCampaigns() {
  return useQuery<CampaignSummary[]>({
    queryKey: INVITE_CAMPAIGNS_KEY,
    queryFn: () => api.get<CampaignSummary[]>("/api/inviter/campaigns"),
    // The worker commits progress chunk by chunk; without polling the list
    // froze on the last loaded numbers until a manual reload.
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.status === "queued" || c.status === "running")
        ? 30_000
        : false,
  });
}

export interface SourceChannelStat {
  channel_id: string;
  username: string | null;
  title: string | null;
  reached: number;
  added: number;
  peer_flood: number;
  success_rate: number;
  peer_flood_rate: number;
  verdict: "good" | "neutral" | "toxic" | "insufficient_data";
}

export function useChannelStats() {
  return useQuery<SourceChannelStat[]>({
    queryKey: ["inviter", "channel-stats"],
    queryFn: () => api.get<SourceChannelStat[]>("/api/inviter/channel-stats"),
  });
}

export function useCampaign(id: string | null) {
  return useQuery<CampaignDetail>({
    queryKey: ["invite_campaigns", id],
    enabled: id !== null,
    queryFn: () => api.get<CampaignDetail>(`/api/inviter/campaigns/${id}`),
    // Same reason as the list: an open campaign card froze on the numbers it
    // was opened with while the row behind it kept moving.
    refetchInterval: (query) =>
      query.state.data?.status === "queued" || query.state.data?.status === "running"
        ? 30_000
        : false,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCampaignPayload) =>
      api.post<CampaignDetail>("/api/inviter/campaigns", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: INVITE_CAMPAIGNS_KEY }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/inviter/campaigns/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: INVITE_CAMPAIGNS_KEY }),
  });
}

export function useRunCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      use_live_telethon?: boolean;
      max_steps?: number;
      // Hand the run to the worker instead of running it inside this request.
      enqueue?: boolean;
    }) =>
      api.post<RunResult>(`/api/inviter/campaigns/${id}/run`, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: INVITE_CAMPAIGNS_KEY });
      qc.invalidateQueries({ queryKey: ["invite_campaigns", vars.id] });
      qc.invalidateQueries({ queryKey: ["invite_attempts", vars.id] });
    },
  });
}

export function useCampaignLifecycle(action: "pause" | "resume" | "cancel") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<CampaignSummary>(`/api/inviter/campaigns/${id}/${action}`),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: INVITE_CAMPAIGNS_KEY });
      qc.invalidateQueries({ queryKey: ["invite_campaigns", id] });
      qc.invalidateQueries({ queryKey: ["invite_attempts", id] });
    },
  });
}

export function useCampaignAttempts(
  campaignId: string | null,
  filter: { status?: AttemptStatus; limit?: number; offset?: number } = {},
  /** Poll while the campaign is live, so the attempts log follows the run. */
  live = false,
) {
  return useQuery<AttemptsPage>({
    queryKey: ["invite_attempts", campaignId, filter],
    enabled: campaignId !== null,
    refetchInterval: live ? 30_000 : false,
    queryFn: () => {
      const params = new URLSearchParams();
      if (filter.status) params.set("status", filter.status);
      if (filter.limit != null) params.set("limit", String(filter.limit));
      if (filter.offset != null) params.set("offset", String(filter.offset));
      const qs = params.toString();
      return api.get<AttemptsPage>(
        `/api/inviter/campaigns/${campaignId}/attempts${qs ? `?${qs}` : ""}`,
      );
    },
  });
}
